import { formatEventDate } from "../events/utils";
import { useFancyClothesDesign } from "../hooks/useFancyClothesDesign";
import { smMarginBottomStyles } from "../neutral/styles/spacing";
import { WebsiteEvent } from "../resources/modelTypes";
import { css } from "@emotion/react";
import { styleUtils } from "@minted/minted-components";
import React, { memo, useMemo } from "react";

const ARROW_HEIGHT = 10;
const POPUP_WIDTH = 252;
const VERTICAL_OFFSET = 55;
const BORDER_COLOR = "rgba(0,0,0,0.2)";
const BORDER_RADIUS = 2;

type InfoPopupEvent = Pick<
  WebsiteEvent,
  | "address1"
  | "address2"
  | "city"
  | "date"
  | "state"
  | "time"
  | "title"
  | "venue"
  | "zipCode"
> & {
  address?: string;
};

type InfoPopupProps = {
  events: InfoPopupEvent[];
  onCloseClick: () => void;
};

const staticStyles = {
  arrow: css`
    border-left: ${ARROW_HEIGHT}px solid transparent;
    border-right: ${ARROW_HEIGHT}px solid transparent;
    border-top: ${ARROW_HEIGHT}px solid #ffffff;
    bottom: -${ARROW_HEIGHT - 1}px;
    box-sizing: border-box;
    left: 50%;
    margin-left: -${ARROW_HEIGHT}px;
    position: absolute;
  `,
  arrowBorder: css`
    border-left: ${ARROW_HEIGHT + 4}px solid transparent;
    border-right: ${ARROW_HEIGHT + 4}px solid transparent;
    border-top: ${ARROW_HEIGHT + 4}px solid ${BORDER_COLOR};
    bottom: -${ARROW_HEIGHT}px;
    box-shadow: rgb(0, 0, 0, 0.3) 0px 1px 4px -1px;
    box-sizing: border-box;
    left: 50%;
    margin-left: -${ARROW_HEIGHT + 4}px;
    position: absolute;
  `,
  box: css`
    bottom: ${VERTICAL_OFFSET}px;
    box-sizing: border-box;
    cursor: default;
    left: -${POPUP_WIDTH / 2}px;
    position: absolute;
    width: ${POPUP_WIDTH}px;
    z-index: 10;
  `,
  boxBorder: css`
    background-color: ${BORDER_COLOR};
    border-radius: ${styleUtils.rem(BORDER_RADIUS)};
    bottom: 0;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
    box-sizing: border-box;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: ${POPUP_WIDTH}px;
  `,
  close: css`
    color: #999999;
    cursor: pointer;
    font-size: ${styleUtils.rem(22)};
    font-weight: 300;
    line-height: 1;
    position: absolute;
    right: ${styleUtils.rem(8)};
    top: ${styleUtils.rem(2)};
  `,
  content: css`
    background-color: #ffffff;
    border-radius: ${styleUtils.rem(BORDER_RADIUS)};
    box-sizing: border-box;
    color: #555555;
    margin: 1px;
    overflow-wrap: break-word;
    padding-bottom: ${styleUtils.rem(10)};
    padding-left: ${styleUtils.rem(15)};
    padding-right: ${styleUtils.rem(15)};
    padding-top: ${styleUtils.rem(10)};
    position: relative;
    width: ${POPUP_WIDTH - 2}px;
  `,
};

const InfoPopup = ({ events, onCloseClick }: InfoPopupProps) => {
  const { designProvider, smallHeadline } = useFancyClothesDesign();

  const textStyles = useMemo(() => {
    const bodyStyle = css`
      font-family: ${designProvider.fontFamily};
      font-size: ${styleUtils.rem(12 * designProvider.fontSizeFactor)};
      line-height: 1.6;
    `;

    const titleStyle = css`
      font-family: ${smallHeadline.fontFamily};
      font-size: ${styleUtils.rem(14 * smallHeadline.fontSizeFactor)};
      line-height: 1.2;
    `;

    return {
      body: bodyStyle,
      title: titleStyle,
    };
  }, [designProvider, smallHeadline]);

  return (
    <div css={staticStyles.box}>
      <div css={staticStyles.boxBorder} />
      <div css={staticStyles.arrowBorder} />
      {events.map((event, eventIndex) => (
        <div css={staticStyles.content} key={eventIndex}>
          <div css={[textStyles.title, smMarginBottomStyles]}>
            {event.title}
          </div>
          <div css={textStyles.body}>
            {event.time && <>{event.time}, </>}
            {formatEventDate(event.date)}
            {event.venue && (
              <>
                <br />
                {event.venue}
              </>
            )}
            <br />
            {event.address}
          </div>
        </div>
      ))}
      <div css={staticStyles.close} onClick={onCloseClick}>
        ×
      </div>
      <div css={staticStyles.arrow} />
    </div>
  );
};

const MemoizedInfoPopup = memo(InfoPopup);

export { MemoizedInfoPopup as InfoPopup };

export default MemoizedInfoPopup;
